import { supabase } from './supabase'
import { compressImage, generateThumbnail } from './imageCompression'

const IMAGE_MIME_TYPES = new Set([
  'image/jpeg','image/png','image/gif','image/webp',
  'image/heic','image/heif','image/tiff','image/bmp',
])

// ── Signed URL cache (55-minute TTL) ──────────────────────────
const urlCache = new Map()  // key: `${bucket}/${path}` → { url, expiresAt }

export function getCachedSignedUrl(bucket, path) {
  const key    = `${bucket}/${path}`
  const cached = urlCache.get(key)
  if (cached && cached.expiresAt > Date.now()) return cached.url
  return null
}

export async function getSignedUrl(bucket, path) {
  const cached = getCachedSignedUrl(bucket, path)
  if (cached) return cached

  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, 3600)  // 60 minutes validity

  if (error) throw error

  const key = `${bucket}/${path}`
  urlCache.set(key, {
    url:       data.signedUrl,
    expiresAt: Date.now() + 55 * 60 * 1000,  // cache for 55 min
  })
  return data.signedUrl
}

// ── Main upload function ───────────────────────────────────────
export async function uploadFile(file, bucket, path, householdId, options = {}) {
  const {
    withThumbnail   = false,
    compressionOpts = {},
  } = options

  // 1. Check storage limit
  const { data: hasRoom, error: limitErr } = await supabase
    .rpc('check_storage_limit', {
      p_household_id: householdId,
      p_file_size:    file.size,
    })
  if (limitErr) throw limitErr
  if (!hasRoom) {
    const err = new Error('Storage limit reached')
    err.code  = 'STORAGE_LIMIT'
    throw err
  }

  const isImage      = IMAGE_MIME_TYPES.has(file.type) || await _looksLikeImage(file)
  let   uploadBlob   = file
  let   finalMime    = file.type
  let   thumbPath    = null

  // 2. Compress if image
  if (isImage) {
    uploadBlob = await compressImage(file, compressionOpts)
    finalMime  = compressionOpts.outputFormat ?? 'image/jpeg'

    // 3. Generate + upload thumbnail
    if (withThumbnail) {
      const thumbBlob = await generateThumbnail(file)
      thumbPath       = path.replace(/(\.[^.]+)?$/, '_thumb.jpg')
      const { error: tErr } = await supabase.storage
        .from(bucket)
        .upload(thumbPath, thumbBlob, { contentType: 'image/jpeg', upsert: false })
      if (tErr) {
        thumbPath = null  // non-fatal: main upload still proceeds
      }
    }
  }

  // 4. Upload main file
  const { error: upErr } = await supabase.storage
    .from(bucket)
    .upload(path, uploadBlob, { contentType: finalMime, upsert: false })

  if (upErr) {
    // Clean up thumb if main upload failed
    if (thumbPath) {
      await supabase.storage.from(bucket).remove([thumbPath]).catch(() => {})
    }
    throw upErr
  }

  return {
    path,
    thumbPath,
    fileSize: uploadBlob.size,
    mimeType: finalMime,
  }
}

// ── Delete a file and its thumbnail ───────────────────────────
export async function deleteFile(bucket, path) {
  const toRemove = [path]
  const thumbPath = path.replace(/(\.[^.]+)?$/, '_thumb.jpg')

  // Optimistically try to remove thumb; ignore 404
  const { data: list } = await supabase.storage.from(bucket).list(
    path.split('/').slice(0, -1).join('/')
  )
  const thumbName = thumbPath.split('/').pop()
  if ((list ?? []).some((f) => f.name === thumbName)) {
    toRemove.push(thumbPath)
  }

  const { error } = await supabase.storage.from(bucket).remove(toRemove)
  if (error) throw error

  // Invalidate cache
  toRemove.forEach((p) => urlCache.delete(`${bucket}/${p}`))
}

// ── Helpers ───────────────────────────────────────────────────
async function _looksLikeImage(file) {
  try {
    const buf   = await file.slice(0, 4).arrayBuffer()
    const bytes = new Uint8Array(buf)
    // JPEG: FF D8 FF; PNG: 89 50 4E 47; GIF: 47 49 46
    return (bytes[0] === 0xFF && bytes[1] === 0xD8) ||
           (bytes[0] === 0x89 && bytes[1] === 0x50) ||
           (bytes[0] === 0x47 && bytes[1] === 0x49)
  } catch {
    return false
  }
}
